"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import { BRAND_COLORS } from "@/lib/email-styles";
import { CONTACT_EMAIL, CONTACT_EMAIL_LINK } from "@/lib/constants";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en-AU">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Something went wrong | CostDoc</title>
      </head>
      <body
        style={{
          margin: 0,
          padding: 0,
          fontFamily: "system-ui, -apple-system, sans-serif",
          backgroundColor: BRAND_COLORS.surface1,
          color: BRAND_COLORS.primary,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "560px",
            margin: "0 auto",
            padding: "2rem 1.5rem",
          }}
        >
          {/* CostDoc wordmark: inline so no image dependency */}
          <a
            href="/"
            aria-label="CostDoc home"
            style={{ display: "inline-block", marginBottom: "2rem", textDecoration: "none" }}
          >
            <span
              style={{
                fontSize: "1.5rem",
                fontWeight: 900,
                letterSpacing: "-0.04em",
                color: BRAND_COLORS.primary,
              }}
            >
              Cost
            </span>
            <span
              style={{
                fontSize: "1.5rem",
                fontWeight: 900,
                letterSpacing: "-0.04em",
                color: BRAND_COLORS.cta,
              }}
            >
              Doc
            </span>
          </a>

          <div
            style={{
              backgroundColor: BRAND_COLORS.surface0,
              border: `1px solid ${BRAND_COLORS.border}`,
              borderRadius: "16px",
              padding: "2.5rem",
              boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                backgroundColor: BRAND_COLORS.signalAmber + "1a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1.25rem",
              }}
            >
              <svg
                width={26}
                height={26}
                viewBox="0 0 24 24"
                fill="none"
                stroke={BRAND_COLORS.signalAmber}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1={12} y1={9} x2={12} y2={13} />
                <line x1={12} y1={17} x2="12.01" y2={17} />
              </svg>
            </div>

            <p
              style={{
                margin: "0 0 0.5rem",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: BRAND_COLORS.cta,
              }}
            >
              Something went wrong
            </p>

            <h1
              style={{
                margin: "0 0 1rem",
                fontSize: "1.5rem",
                fontWeight: 700,
                lineHeight: 1.3,
                color: BRAND_COLORS.primary,
              }}
            >
              An unexpected error occurred
            </h1>

            <p style={{ margin: "0 0 2rem", fontSize: "1rem", color: BRAND_COLORS.secondary, lineHeight: 1.6 }}>
              We&apos;re sorry about that. Your data is safe. This is a temporary problem on our end. Please reload the page to try again.
            </p>

            {error.digest && (
              <p
                style={{
                  margin: "-1rem 0 1.5rem",
                  fontSize: "0.75rem",
                  fontFamily: "ui-monospace, monospace",
                  color: BRAND_COLORS.tertiary,
                }}
              >
                Ref: {error.digest}
              </p>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
              <button
                onClick={reset}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: BRAND_COLORS.cta,
                  color: BRAND_COLORS.surface0,
                  border: "none",
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "0.9375rem",
                  cursor: "pointer",
                  minHeight: 44,
                }}
              >
                Reload page
              </button>
              <a
                href="/"
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: BRAND_COLORS.surface2,
                  color: BRAND_COLORS.primary,
                  border: `1px solid ${BRAND_COLORS.border}`,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "0.9375rem",
                  textDecoration: "none",
                  minHeight: 44,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                Back to homepage
              </a>
            </div>

            <div
              style={{
                margin: "1.5rem 0",
                height: "2px",
                background: "linear-gradient(to right, transparent, rgba(0,0,0,0.05), transparent)",
              }}
            />

            <p style={{ margin: 0, fontSize: "0.875rem", color: BRAND_COLORS.secondary }}>
              Still stuck?{" "}
              <a
                href={CONTACT_EMAIL_LINK}
                style={{ color: BRAND_COLORS.cta, fontWeight: 600 }}
              >
                Email {CONTACT_EMAIL}
              </a>{" "}
              and we&apos;ll get you sorted.
            </p>
          </div>

          {/* Footer strip */}
          <p
            style={{
              marginTop: "1.5rem",
              textAlign: "center",
              fontSize: "0.8125rem",
              color: BRAND_COLORS.tertiary,
            }}
          >
            <a href="/" style={{ color: BRAND_COLORS.cta, fontWeight: 600, textDecoration: "none" }}>
              CostDoc
            </a>
            {" "}: Australia&apos;s independent medical cost transparency platform.
          </p>
        </div>
      </body>
    </html>
  );
}
